import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLiveRefresh } from '../../../context/LiveUpdatesContext';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { PageHeader } from '../../../components';
import { useAuth } from '../../../context/AuthContext';
import { homeAgencyService } from '../../../services/agencyService';
import { FilterTabs, EmptyCard, errorMessage } from '../../agency/shared';
import ConnectionsTab from './ConnectionsTab';
import AgencyStaffTab from './AgencyStaffTab';
import RequestsTab from './RequestsTab';
import { BookingsTab, HoursTab } from './BookingsTabs';

/**
 * Staff > Agencies — booking cover from staffing agencies. Care homes only.
 *
 *   Connections     find an agency by name, request, archive / restore
 *   Agency staff    approve each agency worker for THIS site
 *   Cover requests  ask connected agencies for staff; confirm named offers
 *   Bookings        every confirmed agency shift; attendance
 *   Hours           approve clocked hours; export for the invoice check
 *
 * The tab is in the URL (?tab=) so a notification can open the right one.
 * Everything is for the site being worked in (the top-bar site switcher).
 */

const TABS = ['connections', 'staff', 'requests', 'bookings', 'hours'];

const Agencies = () => {
  const { user } = useAuth();
  const isDomiciliary = (user?.organization?.organization_type || '').toLowerCase() === 'domiciliary';
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get('tab')) ? params.get('tab') : 'connections';
  const setTab = (t) => setParams((p) => { const next = new URLSearchParams(p); next.set('tab', t); return next; }, { replace: true });

  const [connections, setConnections] = useState([]);
  const [overview, setOverview] = useState({});
  const [loading, setLoading] = useState(true);
  const [staffAgencyId, setStaffAgencyId] = useState(null);

  const load = useCallback(async ({ quiet = false } = {}) => {
    try {
      const [c, o] = await Promise.all([homeAgencyService.connections(), homeAgencyService.overview()]);
      setConnections(c.data || []);
      setOverview(o.data || {});
    } catch (err) {
      if (!quiet) toast.error(errorMessage(err, 'Could not load agencies.'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { if (!isDomiciliary) load(); }, [load, isDomiciliary]);
  // Tab counts and connections follow the agency's side as it changes; each
  // tab below refreshes its own list the same way.
  useLiveRefresh(['agency'], () => { if (!isDomiciliary) load({ quiet: true }); });

  const connectedAgencies = useMemo(
    () => connections.filter((c) => c.status === 'connected' && c.agency).map((c) => c.agency),
    [connections],
  );

  if (isDomiciliary) {
    return <EmptyCard title="Agencies are for care homes" text="Booking agency staff onto shifts is available to care-home organisations." />;
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Agencies"
        subtitle="Book cover from staffing agencies. Approve their staff for your home, send cover requests and confirm who comes."
      />
      <FilterTabs
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'connections', label: 'Connections', count: overview.pending_connections || undefined },
          { value: 'staff', label: 'Agency staff', count: overview.staff_to_review || undefined, alert: true },
          { value: 'requests', label: 'Cover requests', count: overview.offers_to_review || undefined, alert: true },
          { value: 'bookings', label: 'Bookings', count: overview.missed || undefined, alert: true },
          { value: 'hours', label: 'Hours', count: overview.hours_to_approve || undefined, alert: true },
        ]}
      />
      {tab === 'connections' && (
        <ConnectionsTab
          rows={connections}
          loading={loading}
          reload={load}
          onViewStaff={(agencyId) => { setStaffAgencyId(agencyId); setTab('staff'); }}
        />
      )}
      {tab === 'staff' && (
        <AgencyStaffTab agencies={connectedAgencies} agencyId={staffAgencyId} setAgencyId={setStaffAgencyId} onChanged={load} />
      )}
      {tab === 'requests' && <RequestsTab agencies={connectedAgencies} onChanged={load} />}
      {tab === 'bookings' && <BookingsTab agencies={connectedAgencies} onChanged={load} />}
      {tab === 'hours' && <HoursTab agencies={connectedAgencies} onChanged={load} />}
    </div>
  );
};

export default Agencies;
